import React, { useCallback, useMemo, useState } from 'react';

import {
  FiltersCollection,
  ScreenAllFilters,
  UnAppliedChanges,
} from 'components/Filters';
import {
  Screen,
  withScreen,
  createScreenRoute,
  SET_FILTERS,
  useScreenDispatch,
} from '@gogame/screens';

import AllFilters from './components/Filters/AllFilters';
import PrimaryFilters from './components/Filters/PrimaryFilters';
import { SCREEN_NAME } from './constants';
import {
  creativesTrackerLink,
  creativesTrackerSearchParams,
} from './navigation';
import { ensureTemporaryState } from './reducers/creativesTrackerTemporaryStateReducer';
import {
  creativesTrackerTemporaryStateReducer,
  SET_ADVANCED_FILTERS,
} from './reducers';
import {
  EMPTY_FILTERS,
  mapStateToPreset,
  mapPresetToState,
  compareSchema,
} from './schemas';

import CreativesTrackerTiles from './components/CreativesTrackerTiles';
import CreativesTrackerApplyButton from './CreativesTrackerApplyButton';
import { ScreenPresets } from 'components/Presets';

const id = 65;

export const permission = 107;
export { mapStateToPreset, mapPresetToState };

const CreativesTracker = withScreen({
  name: SCREEN_NAME,
  searchParams: creativesTrackerSearchParams,
  reducer: creativesTrackerTemporaryStateReducer,
})(({ screenState }) => {
  const [payToLocked, setPayToLocked] = useState(false);
  const dispatch = useScreenDispatch();
  const filtersCollection = useMemo(() => {
    return ensureTemporaryState(screenState).filtersCollection;
  }, [screenState]);

  const handleCompare = (baseFilters) => [
    compareSchema.cast(baseFilters, {
      stripUnknown: true,
      context: {},
    }),
    baseFilters,
  ];

  const handleSetCollection = (filtersCollection) =>
    dispatch({
      type: SET_FILTERS,
      payload: filtersCollection,
    });

  const handleLockPayTo = useCallback(() => {
    setPayToLocked((state) => !state);
  }, []);

  const temporaryAdvancedFilters = screenState.temporary?.advancedFilters;
  const advancedFilters = screenState.advancedFilters;

  const initialAdvancedFilters = temporaryAdvancedFilters || advancedFilters;

  const hasUnappliedAdvancedFilters =
    temporaryAdvancedFilters && temporaryAdvancedFilters !== advancedFilters;

  const handleChangeAdvancedFilters = (value) => {
    dispatch({
      type: SET_ADVANCED_FILTERS,
      payload: value,
    });
  };

  return (
    <React.Fragment>
      <Screen.Toolbar />
      {screenState.ready && screenState.temporary ? <UnAppliedChanges /> : null}
      <Screen.Body>
        <ScreenPresets mapStateToPreset={mapStateToPreset} />

        <FiltersCollection
          screen={SCREEN_NAME}
          controlled={true}
          initialValues={filtersCollection}
          emptyValues={EMPTY_FILTERS}
          onChange={handleSetCollection}
          onCompare={handleCompare}
        >
          <PrimaryFilters
            payToLocked={payToLocked}
            onLockPayTo={handleLockPayTo}
          />
          <ScreenAllFilters
            sidePage={AllFilters}
            badge={!!initialAdvancedFilters?.length}
            params={{
              payToLocked,
              onLockPayTo: handleLockPayTo,
              initialAdvancedFilters: initialAdvancedFilters,
              hasUnappliedAdvancedFilters: hasUnappliedAdvancedFilters,
              onChangeAdvancedFilters: handleChangeAdvancedFilters,
            }}
          />
          <Screen.Toolbar.FiltersApply>
            <CreativesTrackerApplyButton />
          </Screen.Toolbar.FiltersApply>
        </FiltersCollection>
        <CreativesTrackerTiles />
      </Screen.Body>
    </React.Fragment>
  );
});

export default createScreenRoute({
  title: 'Creatives Tracker',
  link: creativesTrackerLink,
  screen: <CreativesTracker />,
  id: id,
  permission: permission,
});
